import { useEffect, useRef, useState } from "react";
import { useBomsByParent } from "@/lib/bom";
import type { BomListItem } from "@/lib/bom";
import { Button } from "@/components/ui/button";
import { BomEditor } from "./BomEditor";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface BomTabPanelProps {
  parentType: "Quote" | "Job";
  parentId: number;
  /**
   * Optional callback to add a new BOM. When provided, an "Add BOM" button is
   * rendered in the sub-tab strip and on the empty state. Pages wire this to
   * open their ProductPicker.
   */
  onAddBom?: () => void;
}

// ---------------------------------------------------------------------------
// BomTabPanel
// ---------------------------------------------------------------------------

export function BomTabPanel({ parentType, parentId, onAddBom }: BomTabPanelProps) {
  const { data: boms, isLoading } = useBomsByParent(parentType, parentId);

  // The selected BOM id — defaults to the first BOM's id when data arrives.
  const [selectedBomId, setSelectedBomId] = useState<number | null>(null);

  // Refs to the tab buttons (keyed by bomId) so arrow-key nav can move focus.
  const tabRefs = useRef<Map<number, HTMLButtonElement>>(new Map());

  // Keep selectedBomId in sync when the list changes.
  // If the previously-selected id is no longer present, fall back to the first.
  useEffect(() => {
    if (!boms || boms.length === 0) {
      setSelectedBomId(null);
      return;
    }

    const ids = boms.map((b) => b.bomId);
    if (selectedBomId === null || !ids.includes(selectedBomId)) {
      setSelectedBomId(boms[0]!.bomId);
    }
  }, [boms, selectedBomId]);

  // ---------------------------------------------------------------------------
  // Loading
  // ---------------------------------------------------------------------------

  if (isLoading) {
    return (
      <div role="status" aria-label="Loading BOMs" className="py-8 flex justify-center">
        <span className="animate-spin text-muted-foreground text-xl" aria-hidden="true">
          ◌
        </span>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Empty
  // ---------------------------------------------------------------------------

  if (!boms || boms.length === 0) {
    return (
      <div className="py-10 flex flex-col items-center gap-3 text-center text-sm text-muted-foreground">
        <p>No BOMs yet — add from a product on the Quote/Order tab.</p>
        {onAddBom ? (
          <Button variant="outline" size="sm" type="button" onClick={onAddBom}>
            Add BOM
          </Button>
        ) : null}
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Sub-tab strip + editor
  // ---------------------------------------------------------------------------

  const activeId = selectedBomId ?? boms[0]!.bomId;
  const activeIndex = Math.max(
    0,
    boms.findIndex((b) => b.bomId === activeId),
  );

  // Stable id helpers so each tab and its panel can cross-reference each other
  // (WAI-ARIA tabs pattern: tab `aria-controls` -> panel id; panel
  // `aria-labelledby` -> tab id).
  const tabId = (bomId: number) => `bom-tab-${bomId}`;
  const panelId = (bomId: number) => `bom-tabpanel-${bomId}`;

  // Roving-tabindex keyboard navigation over the tab buttons ONLY (the trailing
  // "Add BOM" button is not a tab and is excluded). Bound to each tab button's
  // onKeyDown (not the tablist container, which stays non-focusable per ARIA).
  // Arrow keys move both the selection and DOM focus to the target tab;
  // Home/End jump to first/last.
  function handleTablistKeyDown(event: React.KeyboardEvent<HTMLButtonElement>) {
    const count = boms!.length;

    // Navigate relative to the tab that currently has focus (falling back to
    // the selected tab). Keeps arrow nav correct even if focus and selection
    // have momentarily diverged.
    const focusedIndex = boms!.findIndex(
      (b) => tabRefs.current.get(b.bomId) === document.activeElement,
    );
    const originIndex = focusedIndex >= 0 ? focusedIndex : activeIndex;

    let nextIndex: number | null = null;

    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
        nextIndex = (originIndex + 1) % count;
        break;
      case "ArrowLeft":
      case "ArrowUp":
        nextIndex = (originIndex - 1 + count) % count;
        break;
      case "Home":
        nextIndex = 0;
        break;
      case "End":
        nextIndex = count - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    const target = boms![nextIndex]!;
    setSelectedBomId(target.bomId);
    // Move focus to the newly selected tab button.
    tabRefs.current.get(target.bomId)?.focus();
  }

  return (
    <div className="flex flex-col">
      {/* Sub-tab strip */}
      <div
        role="tablist"
        aria-label="BOMs"
        className="flex flex-wrap gap-8 border-b border-border px-2"
      >
        {boms.map((bom: BomListItem) => {
          const label = bom.name ?? `BOM #${bom.bomId}`;
          const isSelected = bom.bomId === activeId;
          return (
            <button
              key={bom.bomId}
              ref={(el) => {
                if (el) tabRefs.current.set(bom.bomId, el);
                else tabRefs.current.delete(bom.bomId);
              }}
              id={tabId(bom.bomId)}
              role="tab"
              aria-selected={isSelected}
              aria-controls={panelId(bom.bomId)}
              tabIndex={isSelected ? 0 : -1}
              data-selected={isSelected ? "true" : undefined}
              onClick={() => setSelectedBomId(bom.bomId)}
              onKeyDown={handleTablistKeyDown}
              className={[
                "inline-flex items-center gap-2 pb-3 text-sm font-semibold transition-all whitespace-nowrap -mb-px",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                isSelected
                  ? "text-primary border-b-2 border-primary"
                  : "text-muted-foreground border-b-2 border-transparent hover:text-foreground hover:border-slate-300",
              ].join(" ")}
              type="button"
            >
              <span>{label}</span>
              <span 
                className={`rounded-full px-2 py-0.5 text-xs tabular-nums ${
                  isSelected ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
                }`}
              >
                {bom.lineItemCount}
              </span>
            </button>
          );
        })}

        {onAddBom ? (
          <Button
            variant="outline"
            size="sm"
            type="button"
            onClick={onAddBom}
            className="ml-auto mb-2 h-8 text-xs font-semibold bg-info/15 text-primary border-transparent hover:bg-info/25"
          >
            + Add BOM
          </Button>
        ) : null}
      </div>

      {/* BOM editor for the active tab */}
      <div
        role="tabpanel"
        id={panelId(activeId)}
        aria-labelledby={tabId(activeId)}
        tabIndex={0}
        className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 pt-4"
      >
        <BomEditor
          bomId={activeId}
          parentType={parentType}
          parentId={parentId}
          productId={null}
        />
      </div>
    </div>
  );
}
